<script setup lang="ts">
import type { CapabilityProfile, UserRow } from '../types/callables'

const props = defineProps<{
  users: UserRow[]
  /** Current admin's uid — their own self-lockout toggles are disabled. */
  selfUid:  string | null
  /** Available profiles, used to render friendly names next to assigned ids. */
  profiles: CapabilityProfile[]
  loading?: boolean
}>()

const emit = defineEmits<{
  toggleActive:   [uid: string, value: boolean]
  toggleAdmin:    [uid: string, value: boolean]
  editAssignments:[user: UserRow]
}>()

/**
 * Look up a profile name for an assigned id. An assignment pointing at a
 * deleted profile shouldn't happen under normal flow (deleteCapabilityProfile
 * batch-detaches), but if it does, render the raw id in a warning style so the
 * admin sees it needs cleaning up rather than a silently missing pill.
 */
function profileName(id: string): string {
  return props.profiles.find((p) => p.id === id)?.name ?? id
}

function isKnownProfile(id: string): boolean {
  return props.profiles.some((p) => p.id === id)
}

function formatDate(iso: string | null): string {
  if (!iso) return '—'
  const d = new Date(iso)
  return Number.isNaN(d.getTime())
    ? '—'
    : d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
}
</script>

<template>
  <div class="wrap">
    <table>
      <thead>
        <tr>
          <th>Email</th>
          <th class="center">Active</th>
          <th class="center">Admin</th>
          <th>Profiles</th>
          <th>Created</th>
          <th>Last sign-in</th>
          <th>Notes</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="u in users" :key="u.uid" :class="{ inactive: !u.active }">
          <td>
            {{ u.email ?? '—' }}
            <span v-if="u.uid === selfUid" class="you">you</span>
            <span v-if="u.disabled" class="flag" title="Disabled at the Firebase Auth level">
              auth-disabled
            </span>
          </td>

          <td class="center check-cell">
            <input
              type="checkbox"
              :checked="u.active"
              :disabled="loading || u.uid === selfUid"
              :title="u.uid === selfUid ? 'You cannot deactivate your own account' : 'Active'"
              @change="emit('toggleActive', u.uid, ($event.target as HTMLInputElement).checked)"
            />
          </td>

          <td class="center check-cell">
            <input
              type="checkbox"
              :checked="u.role === 'admin'"
              :disabled="loading || u.uid === selfUid"
              :title="u.uid === selfUid ? 'You cannot remove your own admin access' : 'Admin'"
              @change="emit('toggleAdmin', u.uid, ($event.target as HTMLInputElement).checked)"
            />
          </td>

          <td class="profiles-cell">
            <span v-if="!(u.profiles?.length)" class="muted">Unrestricted</span>
            <template v-else>
              <span
                v-for="id in u.profiles"
                :key="id"
                class="pill"
                :class="{
                  'is-default': id === u.defaultProfile,
                  'is-missing': !isKnownProfile(id),
                }"
                :title="id === u.defaultProfile ? `${profileName(id)} (default)` : profileName(id)"
              >{{ profileName(id) }}<span v-if="id === u.defaultProfile" class="star" aria-label="default">★</span></span>
            </template>
            <div class="profiles-edit">
              <button
                type="button"
                class="btn btn-link btn-sm"
                :disabled="loading"
                @click="emit('editAssignments', u)"
              >Edit</button>
            </div>
          </td>
          <td class="muted">{{ formatDate(u.createdAt) }}</td>
          <td class="muted">{{ formatDate(u.lastSignInAt) }}</td>
          <td class="muted notes" :title="u.notes">{{ u.notes || '—' }}</td>
        </tr>

        <tr v-if="!users.length && !loading">
          <td colspan="7" class="empty">No users yet.</td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<style scoped>
/* Wide tables scroll inside their own box rather than pushing the page sideways. */
.wrap { overflow-x: auto; }

table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.875rem;
}

th, td {
  text-align: left;
  padding: var(--sp-2) var(--sp-3);
  border-bottom: 1px solid var(--border);
  white-space: nowrap;
  vertical-align: top;
}

th {
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--fg-muted);
  font-weight: 600;
}

.muted { color: var(--fg-muted); }

tr.inactive td:not(.center) { opacity: 0.55; }

/* Checkbox columns are single-glyph; keep them narrow and vertically centred
   so the checkbox lines up with the middle of tall rows (profiles wrap). */
.check-cell {
  width: 1%;             /* shrink-to-fit inside a width:100% table */
  vertical-align: middle;
}

.you {
  margin-left: var(--sp-1);
  padding: 0.05rem 0.3rem;
  border-radius: 3px;
  background: var(--bg-subtle);
  border: 1px solid var(--border);
  font-size: 0.7rem;
  color: var(--fg-muted);
}

.flag {
  margin-left: var(--sp-1);
  padding: 0.05rem 0.3rem;
  border-radius: 3px;
  background: color-mix(in srgb, var(--danger) 14%, transparent);
  color: var(--danger);
  font-size: 0.7rem;
}

.notes {
  max-width: 18rem;
  overflow: hidden;
  text-overflow: ellipsis;
}

.profiles-cell {
  white-space: normal;
  width: 12rem;          /* fixed narrow column — pills stack vertically inside */
}

/* Stack pills vertically; each pill occupies its own row within the cell.
   Aligns to the left so a wrapped column of pills reads top-down cleanly. */
.pill {
  display: flex;
  align-items: center;
  gap: var(--sp-1);
  width: fit-content;
  max-width: 100%;
  padding: 0.1rem 0.5rem;
  margin: 0 0 var(--sp-1);
  border-radius: 999px;
  background: var(--bg-subtle);
  border: 1px solid var(--border);
  font-size: 0.78rem;
  color: var(--fg);
}

.pill.is-default {
  background: color-mix(in srgb, var(--accent) 14%, transparent);
  border-color: color-mix(in srgb, var(--accent) 40%, var(--border));
}

.pill.is-missing {
  background: color-mix(in srgb, var(--danger) 14%, transparent);
  color: var(--danger);
  border-color: color-mix(in srgb, var(--danger) 40%, var(--border));
}

.pill .star { font-size: 0.7rem; }

.profiles-edit {
  margin-top: var(--sp-1);
}

.empty {
  text-align: center;
  color: var(--fg-muted);
  padding: var(--sp-6);
}

input[type='checkbox'] { width: 1rem; height: 1rem; cursor: pointer; }
input[type='checkbox']:disabled { cursor: default; opacity: 0.5; }
</style>
